import { AxiosRequestConfig, AxiosResponse } from "axios";
import { axiosInstance } from "@/plugins/axios";
import GenericTableResponse from "@/models/api/GenericTableResponse";
import { OrderListItem, OrderListResponse } from "@/models/api/orders/OrderListItem";
import OrderItemsListItem, { OrderItemsListResponse } from "@/models/api/orders/OrderItemsListItem";
import CancellableHelper from "@/requests/CancellableHelper";
import GenericTableRequest from "@/models/api/GenericTableRequest";
import OrderItemsAttributesItem from "@/models/api/orders/OrderItemsAttributesItem";
import OrderItemHistoryListItem from "@/models/api/orders/OrderItemHistoryListItem";
import OrderPaymentsListItem from "@/models/api/orders/OrderPaymentsListItem";
import { OrderDocumentResponse } from "@/models/api/orders/OrderDocumentListItem";
import { OrderOwners, OwnerTemporary } from "@/models/api/orders/OrderOwners";
import { EditOrderCommentData, FastCorrectionData, OrderCommentData } from "@/models/api/orders/OrderCommentData";
import { OrderCreateCorrection } from "@/models/api/orders/OrderCreateCorrection";
import { CanIssueReceipt } from "@/models/api/orders/CanIssueReceipt";
import { BookingItemToImport } from "@/models/api/orders/BookingItemToImport";
import { AttributeItem } from "@/models/api/bookings/BookingGetTemporaryItem";
import OrderExportSetting from "@/models/api/orders/OrderExportSetting";
import { OrderItemReceipt, DocumentForReceipt } from "@/models/api/orders/DocumentForReceipt";
import OrderItemPurchaseNetPrice from "@/models/api/orders/OrderItemPurchaseNetPrice";
import { DoubleConfirm } from "@/models/api/DoubleConfirm";
import { StandingOrder } from "@/models/api/administration/contractors/ContractorStandingOrder";
import InformationAboutCorrectedItems from "@/models/api/orders/InformationAboutCorrectedItem";
import { OrderToMergeResponse } from "@/models/api/orders/OrdersToMerge";

//#region Routes
export const URL_GET_ORDERS_PAGED = "/Order/Paged";
export const URL_GET_DATA_FOR_EDIT_PAYMENT = "/payments/get";
export const URL_GET_ORDER_ITEMS_PAGED = "/Order/${OrderId}/OrderItems/Paged";
export const URL_GET_ORDER_DOCUMENTS = "/Order/${OrderId}/Documents/";
export const URL_GET_CONTRACTOR_DOCUMENT_LAYOUT = "/Order/${OrderId}/ContractorDocumentLayout";
export const URL_GET_ORDER_OWNERS = "/Order/${OrderId}/Owners/";
export const URL_GET_ORDER_ISSUE_DATE = "/Order/${OrderId}/IssueDate/";
export const URL_POST_ORDER_ISSUE_DATE = "/Order/${OrderId}/EditIssueDate/";
export const URL_GET_FAST_CORRECTION_DATA = "/Order/${OrderId}/FastCorrectionData/";
export const URL_GET_ORDER_COMMENT_DATA = "/Order/${OrderId}/CommentData/";
export const URL_POST_ORDER_COMMENT_DATA = "/Order/${OrderId}/EditCommentData/";
export const URL_POST_ORDER_OWNERS = "/Order/${OrderId}/EditOwners/";
export const URL_POST_REMOVE_ORDER = "/Order/${OrderId}/Remove";
export const URL_POST_REMOVE_ORDER_DOCUMENTS = "/Order/${OrderId}/RemoveDocuments";
export const URL_GET_ORDER_PAYMENTS = "/Order/${OrderId}/Payments";
export const URL_GET_ORDER_ITEMS_HISTORY = "/Order/${OrderItemId}/HistoryOrderItem";
export const URL_GET_ORDER_ITEMS_ATTRIBUTES = "/Order/${OrderItemId}/OrderItemAttributes";
export const URL_GET_ORDER_ITEMS_ATTRIBUTES_TO_EDIT = "/Order/${OrderItemId}/OrderItemAttributesToEdit";
export const URL_POST_ORDER_ITEMS_ATTRIBUTES = "/Order/${OrderItemId}/EditOrderItemAttributes";
export const URL_POST_CREATE_CORRECTION = "/Order/${OrderId}/CreateCorrection";
export const URL_GET_CHECK_REQUIRE_DATA_TO_GENERATE_DOCUMENT_ORDER_LIST =
  "/Order/${OrderId}/CheckRequireDataToGenerateDocumentOrderList";
export const URL_POST_RECALCULATE_BOOKING_ITEM = "/Order/RecalculateBookingItem";
export const URL_POST_SET_ORDER_ITEM_CASHIER_OWNER = "/Order/${OrderItemId}/SetOrderItemCashierOwner/${CashierOwnerId}";
export const URL_POST_SET_ERROR_SEND_AS_SENDED = "/Order/${OrderId}/SetErrorSendAsSended";
export const URL_GET_INFORMATION_ABOUT_CORRECTED_ITEMS = "/Order/${OrderId}/InformationAboutCorrectedItems";
export const URL_GET_EXPORT_SETTINGS = "/Order/${OrderId}/ExportSettings";
export const URL_POST_EXPORT_ORDER = "/Order/${OrderId}/Export";
export const URL_POST_DISABLE_ACCEPTATION_REMINDER = "/Order/${OrderId}/DisableAcceptationReminder";
export const URL_GET_RECEIPT_ITEMS = "/Order/${OrderId}/GetReceiptItems";
export const URL_POST_GENERATE_DOCUMENT_FOR_RECEIPT = "/Order/${OrderId}/GenerateDocumentForReceipt";
export const URL_POST_REMOVE_LAST_RETURN_PROTOCOL_TO_RECEIPT = "/Order/${OrderId}/RemoveLastReturnProtocolToReceipt";
export const URL_GET_PURCHASE_ORDER_ITEM_NET_PRICE = "/Order/Item/${OrderItemId}/PurchaseNetPrice";
export const URL_POST_EDIT_PURCHASE_NET_PRICE = "/Order/Item/${OrderItemId}/EditPurchaseNetPrice";
export const URL_CREATE_STANDING_ORDER = "/Order/${OrderId}/CreateStandingOrder";
export const URL_GET_ORDERS_TO_MERGE = "/Order/${OrderId}/OrdersToMerge";
export const URL_POST_ORDERS_TO_MERGE = "/Order/${OrderId}/MergeOrders";
//#endregion

const _getPurchaseNetPrice = new CancellableHelper<OrderItemPurchaseNetPrice>(URL_GET_PURCHASE_ORDER_ITEM_NET_PRICE);
export const getPurchaseNetPrice = async (orderItemId: string): Promise<OrderItemPurchaseNetPrice> => {
  return await _getPurchaseNetPrice.get({ OrderItemId: orderItemId });
};

export const getDataForEditPayment = (paymentId: number): Promise<AxiosResponse> => {
  return axiosInstance.get(URL_GET_DATA_FOR_EDIT_PAYMENT, {
    options: { paymentId },
  } as AxiosRequestConfig);
};

const _getOrdersTable = new CancellableHelper<OrderListResponse>(URL_GET_ORDERS_PAGED, OrderListResponse);
_getOrdersTable.jsonSerializer = true;
export const getOrdersTable = async (
  genericTableRequest: GenericTableRequest
): Promise<GenericTableResponse<OrderListItem>> => {
  return await _getOrdersTable.get(genericTableRequest);
};

const _getOrderItemsTable = new CancellableHelper<GenericTableResponse<OrderItemsListItem>>(
  URL_GET_ORDER_ITEMS_PAGED,
  OrderItemsListResponse
);
_getOrderItemsTable.jsonSerializer = true;
export const getOrderItemsTable = async (
  orderId: string,
  genericTableRequest: GenericTableRequest
): Promise<GenericTableResponse<OrderItemsListItem>> => {
  return await _getOrderItemsTable.get({
    OrderId: orderId,
    ...genericTableRequest,
  });
};

const _getOrderItemAttributes = new CancellableHelper<Array<AttributeItem>>(
  URL_GET_ORDER_ITEMS_ATTRIBUTES,
  OrderItemsAttributesItem
);
export const getOrderItemAttributes = async (orderItemId: string): Promise<Array<AttributeItem>> => {
  return await _getOrderItemAttributes.get({
    OrderItemId: orderItemId,
  });
};

const _postSaveOrderItemAttributes = new CancellableHelper<null>(URL_POST_ORDER_ITEMS_ATTRIBUTES, null, true);
export const postSaveOrderItemAttributes = async (
  orderItemId: string,
  attributes: Array<AttributeItem>
): Promise<null> => {
  return await _postSaveOrderItemAttributes.post({ OrderItemId: orderItemId }, attributes);
};

const _getOrderItemAttributesToEdit = new CancellableHelper<Array<AttributeItem>>(
  URL_GET_ORDER_ITEMS_ATTRIBUTES_TO_EDIT,
  AttributeItem
);
export const getOrderItemAttributesToEdit = async (orderItemId: string): Promise<Array<AttributeItem>> => {
  return await _getOrderItemAttributesToEdit.get({
    OrderItemId: orderItemId,
  });
};

const _getOrderItemHistory = new CancellableHelper<Array<OrderItemHistoryListItem>>(
  URL_GET_ORDER_ITEMS_HISTORY,
  OrderItemHistoryListItem
);
export const getOrderItemHistory = async (orderItemId: string): Promise<Array<OrderItemHistoryListItem>> => {
  return await _getOrderItemHistory.get({
    OrderItemId: orderItemId,
  });
};

const _getOrderPayments = new CancellableHelper<Array<OrderPaymentsListItem>>(
  URL_GET_ORDER_PAYMENTS,
  OrderPaymentsListItem
);
export const getOrderPayments = async (orderId: string): Promise<Array<OrderPaymentsListItem>> => {
  return await _getOrderPayments.get({
    OrderId: orderId,
  });
};

const _getOrderDocuments = new CancellableHelper<OrderDocumentResponse>(URL_GET_ORDER_DOCUMENTS, OrderDocumentResponse);
export const getOrderDocuments = async (orderId: string): Promise<OrderDocumentResponse> => {
  return await _getOrderDocuments.get({
    OrderId: orderId,
  });
};

const _getContractorDocumentLayout = new CancellableHelper<string>(URL_GET_CONTRACTOR_DOCUMENT_LAYOUT, "string");
export const getContractorDocumentLayout = async (orderId: string): Promise<string> => {
  return await _getContractorDocumentLayout.get({
    OrderId: orderId,
  });
};

const _getOrderOwners = new CancellableHelper<OrderOwners>(URL_GET_ORDER_OWNERS, OwnerTemporary);
export const getOrderOwners = async (orderId: string): Promise<OrderOwners> => {
  return await _getOrderOwners.get({
    OrderId: orderId,
  });
};

const _editOrderOwners = new CancellableHelper<string>(URL_POST_ORDER_OWNERS);
export const editOrderOwners = async (orderId: string, orderOwners: OrderOwners): Promise<string> => {
  return await _editOrderOwners.post({ OrderId: orderId }, orderOwners);
};

const _removeOrder = new CancellableHelper<null>(URL_POST_REMOVE_ORDER, null, true);
export const removeOrder = async (orderId: string, cancelBookingItem: boolean): Promise<null> => {
  // _removeOrder.url = URL_POST_REMOVE_ORDER.replace("${OrderId}", orderId);
  return await _removeOrder.post({ OrderId: orderId, cancelBookingItem: cancelBookingItem }, {});
};

const _removeOrderDocuments = new CancellableHelper<DoubleConfirm>(URL_POST_REMOVE_ORDER_DOCUMENTS, null, true);
export const removeOrderDocuments = async (orderId: string, confirmed: boolean): Promise<DoubleConfirm> => {
  return await _removeOrderDocuments.post({
    OrderId: orderId,
    confirmed: confirmed,
  });
};

const _getFastCorrectionData = new CancellableHelper<FastCorrectionData>(
  URL_GET_FAST_CORRECTION_DATA,
  FastCorrectionData
);
export const getFastCorrectionData = async (orderId: string): Promise<FastCorrectionData> => {
  return await _getFastCorrectionData.get({
    OrderId: orderId,
  });
};

const _getOrderCommentData = new CancellableHelper<OrderCommentData>(URL_GET_ORDER_COMMENT_DATA, OrderCommentData);
export const getOrderCommentData = async (orderId: string): Promise<OrderCommentData> => {
  return await _getOrderCommentData.get({
    OrderId: orderId,
  });
};

const _editOrderCommentData = new CancellableHelper<null>(URL_POST_ORDER_COMMENT_DATA, null, true);
export const editOrderCommentData = async (orderId: string, data: EditOrderCommentData): Promise<null> => {
  return await _editOrderCommentData.post({ OrderId: orderId }, data);
};

const _getOrderIssueDate = new CancellableHelper<string>(URL_GET_ORDER_ISSUE_DATE);
export const getOrderIssueDate = async (orderId: string): Promise<string> => {
  return await _getOrderIssueDate.get({
    OrderId: orderId,
  });
};

const _editOrderIssueDate = new CancellableHelper<null>(URL_POST_ORDER_ISSUE_DATE, null, true);
export const editOrderIssueDate = async (orderId: string, issueDate: string): Promise<null> => {
  return await _editOrderIssueDate.post({
    OrderId: orderId,
    issueDate: issueDate,
  });
};

const _createCorrection = new CancellableHelper<null>(URL_POST_CREATE_CORRECTION, null, true);
export const createCorrection = async (orderId: string, params: OrderCreateCorrection): Promise<null> => {
  return await _createCorrection.post({ OrderId: orderId }, params);
};

const _checkRequireDataToGenerateDocumentOrderList = new CancellableHelper<CanIssueReceipt>(
  URL_GET_CHECK_REQUIRE_DATA_TO_GENERATE_DOCUMENT_ORDER_LIST,
  CanIssueReceipt
);
export const checkRequireDataToGenerateDocumentOrderList = async (
  orderId: string,
  timestamp: string | null = null
): Promise<CanIssueReceipt> => {
  return await _checkRequireDataToGenerateDocumentOrderList.get({
    OrderId: orderId,
    timestamp: timestamp,
  });
};

const _recalculateBookingItem = new CancellableHelper<BookingItemToImport>(
  URL_POST_RECALCULATE_BOOKING_ITEM,
  BookingItemToImport
);
export const recalculateBookingItem = async (
  bookingItemToImport: BookingItemToImport
): Promise<BookingItemToImport> => {
  return await _recalculateBookingItem.post({}, bookingItemToImport);
};

const _setOrderItemCashierOwner = new CancellableHelper<null>(URL_POST_SET_ORDER_ITEM_CASHIER_OWNER, null, true);
export const setOrderItemCashierOwner = async (orderItemId: string, cashierOwnerId: number | null): Promise<null> => {
  return await _setOrderItemCashierOwner.post({ OrderItemId: orderItemId, CashierOwnerId: cashierOwnerId }, {});
};

const _editPurchaseNetPrice = new CancellableHelper<null>(URL_POST_EDIT_PURCHASE_NET_PRICE, null, true);
export const editPurchaseNetPrice = async (
  orderItemId: string,
  purchaseNetPrice: OrderItemPurchaseNetPrice
): Promise<null> => {
  return await _editPurchaseNetPrice.post(
    {
      OrderItemId: orderItemId,
    },
    purchaseNetPrice
  );
};

const _setErrorSendAsSended = new CancellableHelper<null>(URL_POST_SET_ERROR_SEND_AS_SENDED, null, true);
export const setErrorSendAsSended = async (orderId: string): Promise<null> => {
  return await _setErrorSendAsSended.post({ OrderId: orderId });
};

const _getInformationAboutCorrectedItems = new CancellableHelper<Array<InformationAboutCorrectedItems>>(
  URL_GET_INFORMATION_ABOUT_CORRECTED_ITEMS,
  InformationAboutCorrectedItems
);
export const getInformationAboutCorrectedItems = async (
  orderId: string
): Promise<Array<InformationAboutCorrectedItems>> => {
  return _getInformationAboutCorrectedItems.get({
    OrderId: orderId,
  });
};

const _getExportSettings = new CancellableHelper<OrderExportSetting>(URL_GET_EXPORT_SETTINGS, OrderExportSetting);
export const getExportSettings = async (orderId: string): Promise<OrderExportSetting> => {
  return _getExportSettings.get({
    OrderId: orderId,
  });
};

const _exportOrder = new CancellableHelper<void>(URL_POST_EXPORT_ORDER, null, true);
export const exportOrder = async (
  orderId: string,
  exportCostDocument: boolean,
  exportOrder: boolean,
  exportCostDocumentOther: boolean
) => {
  return _exportOrder.post({
    OrderId: orderId,
    exportCostDocument: exportCostDocument,
    exportOrder: exportOrder,
    exportCostDocumentOther: exportCostDocumentOther,
  });
};

const _disableAcceptationReminder = new CancellableHelper<null>(URL_POST_DISABLE_ACCEPTATION_REMINDER, null, true);
export const disableAcceptationReminder = async (orderId: string): Promise<null> => {
  return _disableAcceptationReminder.post({ OrderId: orderId });
};

const _getReceiptItems = new CancellableHelper<Array<OrderItemReceipt>>(URL_GET_RECEIPT_ITEMS, OrderItemReceipt);
export const getReceiptItems = async (orderId: string): Promise<Array<OrderItemReceipt>> => {
  return _getReceiptItems.get({
    OrderId: orderId,
  });
};

const _removeLastReturnProtocolToReceipt = new CancellableHelper<void>(
  URL_POST_REMOVE_LAST_RETURN_PROTOCOL_TO_RECEIPT,
  null,
  true
);
export const removeLastReturnProtocolToReceipt = async (orderId: string) => {
  return _removeLastReturnProtocolToReceipt.post({ OrderId: orderId }, null);
};

const _generateDocumentForReceipt = new CancellableHelper<string>(URL_POST_GENERATE_DOCUMENT_FOR_RECEIPT, "string");
export const generateDocumentForReceipt = async (orderId: string, documentForReceipt: DocumentForReceipt) => {
  return _generateDocumentForReceipt.post({ OrderId: orderId }, documentForReceipt);
};

const _addStandingOrder = new CancellableHelper<null>(URL_CREATE_STANDING_ORDER);
export const createStandingOrder = async (orderId: string, standingOrder: StandingOrder) => {
  return _addStandingOrder.post(
    {
      OrderId: orderId,
    },
    standingOrder
  );
};

const _getOrdersToMerge = new CancellableHelper<OrderToMergeResponse>(URL_GET_ORDERS_TO_MERGE, OrderToMergeResponse);
_getOrdersToMerge.jsonSerializer = true;
export const getOrdersToMerge = async (
  orderId: string,
  genericTableRequest: GenericTableRequest
): Promise<OrderToMergeResponse> => {
  return await _getOrdersToMerge.get({
    OrderId: orderId,
    ...genericTableRequest,
  });
};

const _mergeOrders = new CancellableHelper<null>(URL_POST_ORDERS_TO_MERGE, null, true);
export const mergeOrders = async (orderId: string, ordersToMerge: Array<string>) => {
  return _mergeOrders.post({ OrderId: orderId }, ordersToMerge);
};
